import { useQueryClient } from "@tanstack/react-query";
import bem from "easy-bem";
import type {
  Restriction,
  RestrictionType,
} from "features/restrictions/data/types";
import { RESTRICTION_LABELS } from "features/restrictions/data/types";
import React, { useState } from "react";
import { formatDate } from "utils/basic";

import { useLiftRestriction } from "../../hooks/useLiftRestriction";
import { useSetRestriction } from "../../hooks/useSetRestriction";

import "./RestrictionsPanel.css";

const cn = bem("restrictions-panel");

const RESTRICTION_TYPES = Object.keys(RESTRICTION_LABELS) as RestrictionType[];

type RestrictionsPanelProps = {
  userId: number;
  active: Restriction[];
  history: Restriction[];
};

const RestrictionsPanel: React.FC<RestrictionsPanelProps> = ({
  userId,
  active,
  history,
}) => {
  const queryClient = useQueryClient();

  const bumpRefresh = () =>
    queryClient.invalidateQueries({
      queryKey: [`restrictions/user/${userId}/`],
    });

  const {
    isSaving,
    error: setError,
    setRestriction,
  } = useSetRestriction(bumpRefresh);
  const {
    liftingId,
    error: liftError,
    liftRestriction,
  } = useLiftRestriction(bumpRefresh);

  const [newType, setNewType] = useState<RestrictionType>("ban_comment");
  const [newReason, setNewReason] = useState("");
  const [newExpiresAt, setNewExpiresAt] = useState("");

  const [liftReasonById, setLiftReasonById] = useState<Record<number, string>>(
    {},
  );

  const onAddRestriction = async () => {
    if (!newReason.trim()) return;

    await setRestriction(
      userId,
      newType,
      newReason.trim(),
      newExpiresAt ? new Date(newExpiresAt).toISOString() : null,
    );
    setNewReason("");
    setNewExpiresAt("");
  };

  return (
    <div className={cn()}>
      {active.length > 0 && (
        <div className={cn("active-list")}>
          {active.map((restriction) => (
            <div
              key={restriction.id}
              className={cn("active-item")}
            >
              <div className={cn("active-item-header")}>
                <strong>{RESTRICTION_LABELS[restriction.type]}</strong>
                <span>
                  {restriction.expiresAt
                    ? `до ${formatDate(restriction.expiresAt)}`
                    : "бессрочно"}
                </span>
              </div>

              {restriction.reason && <p>{restriction.reason}</p>}

              <div className={cn("row")}>
                <input
                  className={cn("input")}
                  placeholder="Причина снятия"
                  value={liftReasonById[restriction.id] ?? ""}
                  onChange={(e) =>
                    setLiftReasonById((prev) => ({
                      ...prev,
                      [restriction.id]: e.target.value,
                    }))
                  }
                />
                <button
                  type="button"
                  className={cn("lift-button")}
                  disabled={liftingId === restriction.id}
                  onClick={() =>
                    liftRestriction(
                      restriction.id,
                      liftReasonById[restriction.id] ?? "",
                    )
                  }
                >
                  Снять
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={cn("new-form")}>
        <select
          className={cn("select")}
          value={newType}
          onChange={(e) => setNewType(e.target.value as RestrictionType)}
        >
          {RESTRICTION_TYPES.map((type) => (
            <option
              key={type}
              value={type}
            >
              {RESTRICTION_LABELS[type]}
            </option>
          ))}
        </select>

        <input
          className={cn("input")}
          placeholder="Причина"
          value={newReason}
          onChange={(e) => setNewReason(e.target.value)}
        />

        <input
          className={cn("input")}
          type="datetime-local"
          value={newExpiresAt}
          onChange={(e) => setNewExpiresAt(e.target.value)}
        />

        <button
          type="button"
          className={cn("add-button")}
          disabled={isSaving || !newReason.trim()}
          onClick={onAddRestriction}
        >
          Применить
        </button>
      </div>

      {(setError || liftError) && (
        <div className={cn("error")}>{setError || liftError}</div>
      )}

      {history.length > 0 && (
        <div className={cn("history")}>
          <div className={cn("history-title")}>История</div>
          {history.map((restriction) => (
            <div
              key={restriction.id}
              className={cn("history-item")}
            >
              {RESTRICTION_LABELS[restriction.type]}
              {restriction.reason ? `: ${restriction.reason}` : ""} — снято{" "}
              {restriction.liftedAt ? formatDate(restriction.liftedAt) : "—"}
              {restriction.liftReason ? ` (${restriction.liftReason})` : ""}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RestrictionsPanel;
